import {
  Button,
  Divider,
  Error,
  Form,
  Input,
  InputGroup,
  Label,
  LoadingSpinner,
  Modal,
  Switch,
  Text
} from '@opengov/component-library/capital';
import { DEFAULT_STORY_MARGINS } from '@opengov/stories-shared/constants';
import { IPublication, IStory, IStoryContent, IStoryMargins } from '@opengov/stories-shared/types';
import { clamp, debounce, isEqual } from 'lodash';
import React, { createRef } from 'react';

import { IExportStoryProps, IModalMessages, IPreviewPdfProps, ISettings } from '../../../types';

import styles from './ExportPdfOptions.scss';

export const STORY_MINIMUM_MARGIN_INCHES = 0;
export const STORY_MAXIMUM_MARGIN_INCHES = 3;

export function parseMarginValue(stringValue: string): number | undefined {
  const floatValue = parseFloat(stringValue.replace(/[$¢€,]+/g, ''));

  if (isNaN(floatValue)) {
    return undefined;
  }

  return clamp(floatValue, STORY_MINIMUM_MARGIN_INCHES, STORY_MAXIMUM_MARGIN_INCHES);
}

export interface IExportPdfOptionsProps {
  open: boolean;
  isAdmin: boolean;
  isTemplate: boolean;
  storyMarginsEnabled: boolean;
  hasGallery: boolean;
  messages: IModalMessages;
  isPreviewingPdf: boolean;
  isPreviewingPdfFailure: boolean;
  settings: ISettings;
  story: IStory;
  publication?: IPublication;
  entitySubdomain: string;
  storyContents: IStoryContent[];
  onClose(): void;
  onExport(props: IExportStoryProps): any;
  // Resolves to the preview PDF URL, or '' when the request was aborted
  // because a newer preview is already in flight. Rejects on a real failure.
  previewPdf(props: IPreviewPdfProps): Promise<string>;
}

export interface IExportPdfOptionsState {
  displayTitle: boolean;
  displayDate: boolean;
  displayPageNumber: boolean;
  displayStartingPageNumber: boolean;
  error: string;
  enlargeFonts: boolean;
  startingPage: string;
  pdfPreviewLink: string;
  margins: IStoryMargins;
  showHeader: boolean;
  showFooter: boolean;
}

export class ExportPdfOptions extends React.Component<IExportPdfOptionsProps, IExportPdfOptionsState> {
  controllerRef: React.MutableRefObject<AbortController>;

  generatePreview = debounce(
    (options?: Partial<IPreviewPdfProps>) => {
      const {
        displayTitle,
        displayDate,
        displayPageNumber,
        enlargeFonts,
        margins,
        startingPage,
        showFooter,
        showHeader
      } = this.state;
      const {
        previewPdf,
        settings,
        story,
        entitySubdomain,
        storyContents,
        hasGallery,
        isTemplate,
        storyMarginsEnabled
      } = this.props;

      if (this.controllerRef.current) {
        this.controllerRef.current.abort();
      }

      const controller = new AbortController();
      this.controllerRef.current = controller;

      previewPdf({
        displayDate,
        displayPageNumber,
        entitySubdomain,
        displayTitle,
        enlargeFonts,
        hasGallery,
        settings,
        isTemplate,
        startingPage,
        story,
        storyContents,
        storyMarginsEnabled,
        margins,
        controller: this.controllerRef.current,
        showFooter,
        showHeader,
        ...options
      })
        .then((pdfPreviewLink) => {
          this.setState({ pdfPreviewLink });
        })
        .catch(() => {
          // The preview mutations reject on a real failure (aborts resolve to '').
          // Clear the stale link; the failure message is driven by the
          // `isPreviewingPdfFailure` prop off the mutation's error state.
          this.setState({ pdfPreviewLink: '' });
        });
    },
    1000,
    { trailing: true }
  );

  constructor(props: IExportPdfOptionsProps) {
    super(props);

    this.controllerRef = createRef<AbortController | null>();
    this.controllerRef.current = new AbortController();

    this.state = {
      displayTitle: false,
      displayDate: false,
      displayPageNumber: false,
      enlargeFonts: false,
      displayStartingPageNumber: false,
      pdfPreviewLink: '',
      error: '',
      startingPage: '1',
      margins: { ...DEFAULT_STORY_MARGINS },
      showHeader: true,
      showFooter: true
    };
  }

  componentDidMount() {
    this.generatePreview({
      margins: { ...DEFAULT_STORY_MARGINS }
    });
  }

  componentDidUpdate(prevProps: IExportPdfOptionsProps) {
    if (!isEqual(prevProps.story, this.props.story)) {
      this.generatePreview();
    }
  }

  onDisplayDateToggle = () => {
    const { displayDate } = this.state;
    const newDisplayDate = !displayDate;

    this.generatePreview({ displayDate: newDisplayDate });

    this.setState({ displayDate: newDisplayDate });
  };

  onDisplayPageNumberToggle = () => {
    const { displayPageNumber, displayStartingPageNumber, startingPage } = this.state;
    const newDisplayPageNumber = !displayPageNumber;
    const newdisplayStartingPageNumber = !displayStartingPageNumber;
    const newStartingPage = startingPage === '' ? '1' : startingPage;

    this.generatePreview({ displayPageNumber: newDisplayPageNumber });

    this.setState({
      displayPageNumber: newDisplayPageNumber,
      displayStartingPageNumber: newdisplayStartingPageNumber,
      startingPage: newStartingPage
    });
  };

  onDisplayTitleToggle = () => {
    const { displayTitle } = this.state;
    const newdisplayTitle = !displayTitle;

    this.generatePreview({
      displayTitle: newdisplayTitle
    });

    this.setState({ displayTitle: newdisplayTitle });
  };

  onEnlargeFontsToggle = () => {
    const { enlargeFonts } = this.state;
    const newEnlargeFonts = !enlargeFonts;

    this.generatePreview({
      enlargeFonts: newEnlargeFonts
    });

    this.setState({ enlargeFonts: newEnlargeFonts });
  };

  onStartingNumberChange = (value) => {
    const { messages } = this.props;

    let error = '';
    if (value && value < 1) {
      error = messages.startingPageMinError;
    }
    if (value > 5000) {
      error = messages.startingPageMaxError;
    }
    if (value % 1 !== 0) {
      error = messages.startingPageWholeNumberError;
    }

    if (!error) {
      this.generatePreview({
        startingPage: value
      });
    } else {
      this.generatePreview({
        startingPage: null
      });
    }

    this.setState({ startingPage: value, error: error });
  };

  handleFooterToggle = () => {
    const { showFooter } = this.state;
    const newshowFooter = !showFooter;

    this.generatePreview({
      showFooter: newshowFooter
    });

    this.setState({ showFooter: newshowFooter });
  };
  handleHeaderToggle = () => {
    const { showHeader } = this.state;
    const newshowHeader = !showHeader;

    this.generatePreview({
      showHeader: newshowHeader
    });

    this.setState({ showHeader: newshowHeader });
  };

  handleSubmit = () => {
    const { onExport, hasGallery, settings, story, storyContents, storyMarginsEnabled } = this.props;
    const {
      displayDate,
      displayPageNumber,
      displayTitle,
      enlargeFonts,
      startingPage,
      margins,
      showHeader,
      showFooter
    } = this.state;

    onExport({
      storyMarginsEnabled,
      displayDate,
      displayPageNumber,
      startingPage,
      displayTitle,
      enlargeFonts,
      hasGallery,
      settings,
      story,
      storyContents,
      margins,
      showHeader,
      showFooter
    });
  };

  handleUpdateMargins = (key: keyof IStoryMargins, value: string) => {
    const updatedMargins = { ...this.state.margins, [key]: parseMarginValue(value) };

    this.setState({
      margins: updatedMargins
    });

    this.generatePreview();
  };

  renderMarginsSection = () => {
    const { messages } = this.props;
    const { margins } = this.state;
    return (
      <div className={styles.marginsSection} data-test="story-export-margins">
        <Label variant="group" className={styles.title}>
          {messages.adjustPageMargins}
        </Label>
        <div className={styles.spacer} />
        <Form.Row>
          <Form.Item label={messages.topMargin}>
            <InputGroup>
              <Input.Number
                step={0.1}
                placeholder={`${DEFAULT_STORY_MARGINS.marginTop}`}
                dataTest="story-export-top-margin"
                value={margins.marginTop ?? ''}
                onChange={(value) => this.handleUpdateMargins('marginTop', value)}
              />
              <InputGroup.Suffix>
                <InputGroup.Text>{messages.unitInches}</InputGroup.Text>
              </InputGroup.Suffix>
            </InputGroup>
          </Form.Item>
          <div className={styles.inlineSpacer} />
          <Form.Item label={messages.bottomMargin}>
            <InputGroup>
              <Input.Number
                step={0.1}
                placeholder={`${DEFAULT_STORY_MARGINS.marginBottom}`}
                dataTest="story-export-bottom-margin"
                value={margins.marginBottom ?? ''}
                onChange={(value) => this.handleUpdateMargins('marginBottom', value)}
              />
              <InputGroup.Suffix>
                <InputGroup.Text>{messages.unitInches}</InputGroup.Text>
              </InputGroup.Suffix>
            </InputGroup>
          </Form.Item>
        </Form.Row>
        <div className={styles.spacer} />
        <Form.Row>
          <Form.Item label={messages.leftMargin}>
            <InputGroup>
              <Input.Number
                step={0.1}
                placeholder={`${DEFAULT_STORY_MARGINS.marginLeft}`}
                dataTest="story-export-left-margin"
                value={margins.marginLeft ?? ''}
                onChange={(value) => this.handleUpdateMargins('marginLeft', value)}
              />
              <InputGroup.Suffix>
                <InputGroup.Text>{messages.unitInches}</InputGroup.Text>
              </InputGroup.Suffix>
            </InputGroup>
          </Form.Item>
          <div className={styles.inlineSpacer} />
          <Form.Item label={messages.rightMargin}>
            <InputGroup>
              <Input.Number
                step={0.1}
                placeholder={`${DEFAULT_STORY_MARGINS.marginRight}`}
                dataTest="story-export-right-margin"
                value={margins.marginRight ?? ''}
                onChange={(value) => this.handleUpdateMargins('marginRight', value)}
              />
              <InputGroup.Suffix>
                <InputGroup.Text>{messages.unitInches}</InputGroup.Text>
              </InputGroup.Suffix>
            </InputGroup>
          </Form.Item>
        </Form.Row>
      </div>
    );
  };

  render() {
    if (!this.props.open) return null;
    const { onClose, isPreviewingPdf, isPreviewingPdfFailure, messages, storyMarginsEnabled } = this.props;
    const {
      displayTitle,
      displayDate,
      displayPageNumber,
      enlargeFonts,
      pdfPreviewLink,
      startingPage,
      displayStartingPageNumber,
      error,
      showFooter,
      showHeader
    } = this.state;

    return (
      <Modal.Work width="wide" title={messages.pdfExport} onClose={onClose}>
        <div className={styles.container}>
          {/* Form section */}
          <div className={styles.formContainer}>
            <div className={styles.divider}>
              <Divider.Horizontal />
            </div>
            <div className={styles.toggle}>
              <Switch.Small
                id="toggle-header"
                dataTest="toggle-header"
                label="Show Header"
                checked={showHeader}
                onChange={this.handleHeaderToggle}
              />
            </div>
            <div className={styles.toggle}>
              <Switch.Small
                id="toggle-footer"
                dataTest="toggle-footer"
                label="Show Footer"
                checked={showFooter}
                onChange={this.handleFooterToggle}
              />
            </div>
            <div className={styles.divider}>
              <Divider.Horizontal />
            </div>
            <Label variant="group" className={styles.title}>
              {messages.fonts}
            </Label>
            <div className={styles.toggle}>
              <Switch.Small
                id="toggle-enlarge-fonts"
                dataTest="toggle-enlarge-fonts"
                label={messages.enlargeSizes}
                checked={enlargeFonts}
                onChange={this.onEnlargeFontsToggle}
              />
            </div>
            <div className={styles.divider}>
              <Divider.Horizontal />
            </div>
            <Label variant="group" className={styles.title}>
              {messages.pageFootnotes}
            </Label>
            <div className={styles.toggle}>
              <Switch.Small
                id="toggle-title"
                dataTest="toggle-title"
                label={messages.storyTitle}
                checked={displayTitle}
                onChange={this.onDisplayTitleToggle}
              />
            </div>
            <div className={styles.toggle}>
              <Switch.Small
                id="toggle-date"
                dataTest="toggle-date"
                label={messages.dateOfExport}
                checked={displayDate}
                onChange={this.onDisplayDateToggle}
              />
            </div>
            <div className={styles.toggle}>
              <Switch.Small
                id="toggle-page-number"
                dataTest="toggle-page-number"
                label={messages.pageNumber}
                checked={displayPageNumber}
                onChange={this.onDisplayPageNumberToggle}
              />
            </div>
            {displayStartingPageNumber && (
              <div className={styles.inputGroup}>
                <Form.Item label={messages.startingPageNumber}>
                  <Input.Number
                    className={styles.startingPageInput}
                    size="small"
                    value={startingPage || ''}
                    onChange={this.onStartingNumberChange}
                    dataTest={'input-starting-page-number'}
                  />
                </Form.Item>
                <Error dataTest={'error-starting-page-number'}>{error}</Error>
              </div>
            )}
            <div className={styles.divider}>
              <Divider.Horizontal />
            </div>
            {storyMarginsEnabled && this.renderMarginsSection()}
          </div>

          <div className={styles.divider}>
            <Divider.Vertical />
          </div>

          {/* Preview section */}
          <div className={styles.previewContainer}>
            <Label variant="h2">{messages.preview}</Label>
            <div id="pdfPreview" className={styles.iframeContainer}>
              {isPreviewingPdf && (
                <div className={styles.loader} data-test="story-exporting-pdf">
                  <LoadingSpinner dataTest="pdf-preview-loader" message={messages.pdfPreviewText} variant="page" />
                </div>
              )}

              {!isPreviewingPdf && !isPreviewingPdfFailure && pdfPreviewLink && (
                <iframe
                  className={
                    styles.preview
                  }
                  width="100%"
                  height="90%"
                  src={pdfPreviewLink}
                  name="story"
                />
              )}

              {isPreviewingPdfFailure && <Text>{messages.pdfPreviewFailureText}</Text>}
            </div>
          </div>
        </div>
        <div className={styles.bottomContainer}>
          <Button onClick={onClose} dataTest="button-cancel">
            {messages.cancel}
          </Button>
          <Button
            variant="primary"
            onClick={this.handleSubmit}
            dataTest="button-export"
            disabled={error ? true : false}
          >
            {messages.export}
          </Button>
        </div>
      </Modal.Work>
    );
  }
}
